import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import {
  useHelpCategories,
  useAdminHelpArticles,
  useCreateHelpCategory,
  useUpdateHelpCategory,
  useDeleteHelpCategory,
  useReorderHelpCategories,
  HelpCategory,
} from "@/hooks/useHelpCenter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  ArrowLeft,
  GripVertical,
  FolderOpen,
  FileText,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const ICON_OPTIONS = [
  "Rocket",
  "User",
  "LayoutDashboard",
  "Users",
  "GraduationCap",
  "BookOpen",
  "Calendar",
  "MessageSquare",
  "Shield",
  "HelpCircle",
  "Settings",
  "Bell",
  "Heart",
  "Star",
  "Zap",
];

// Generate slug from name
function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

interface SortableRowProps {
  category: HelpCategory;
  articleCount: number;
  onEdit: (category: HelpCategory) => void;
  onDelete: (category: HelpCategory) => void;
}

function SortableRow({ category, articleCount, onEdit, onDelete }: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow ref={setNodeRef} style={style} className={isDragging ? "bg-muted" : ""}>
      <TableCell className="w-[50px]">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded"
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <span className="font-medium">{category.name}</span>
          {!category.is_active && (
            <Badge variant="secondary" className="text-xs">Inactive</Badge>
          )}
        </div>
      </TableCell>
      <TableCell className="text-muted-foreground text-sm">
        {generateSlug(category.name)}
      </TableCell>
      <TableCell className="max-w-[200px] truncate text-muted-foreground text-sm">
        {category.description || "—"}
      </TableCell>
      <TableCell>
        <Badge variant="outline" className="gap-1">
          <FileText className="h-3 w-3" />
          {articleCount}
        </Badge>
      </TableCell>
      <TableCell className="text-center text-muted-foreground">
        {category.sort_order}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => onEdit(category)}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-destructive hover:text-destructive"
            onClick={() => onDelete(category)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

export default function CategoriesManagement() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  // Check if user is admin
  const { data: isAdmin, isLoading: adminLoading } = useQuery({
    queryKey: ["is_admin", user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      const { data } = await supabase.rpc("has_role", { _user_id: user.id, _role: "admin" });
      return data;
    },
    enabled: !!user?.id,
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!adminLoading && isAdmin === false) {
      navigate("/dashboard");
    }
  }, [isAdmin, adminLoading, navigate]);

  const { data: categories, isLoading: categoriesLoading } = useHelpCategories();
  const { data: articles, isLoading: articlesLoading } = useAdminHelpArticles();
  const createCategory = useCreateHelpCategory();
  const updateCategory = useUpdateHelpCategory();
  const deleteCategory = useDeleteHelpCategory();
  const reorderCategories = useReorderHelpCategories();

  const [editingCategory, setEditingCategory] = useState<HelpCategory | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<HelpCategory | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [localCategories, setLocalCategories] = useState<HelpCategory[]>([]);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    icon: "HelpCircle",
    is_active: true,
  });

  // Sync local categories when data loads
  useMemo(() => {
    if (categories) {
      setLocalCategories([...categories]);
    }
  }, [categories]);

  // Count articles per category
  const articleCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    articles?.forEach((article) => {
      if (!article.is_archived) {
        counts[article.category_id] = (counts[article.category_id] || 0) + 1;
      }
    });
    return counts;
  }, [articles]);

  // DnD Kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = localCategories.findIndex((c) => c.id === active.id);
      const newIndex = localCategories.findIndex((c) => c.id === over.id);

      const newOrder = arrayMove(localCategories, oldIndex, newIndex);
      setLocalCategories(newOrder);

      // Update sort_order values and save
      const updates = newOrder.map((cat, index) => ({
        id: cat.id,
        sort_order: index,
      }));

      reorderCategories.mutate(updates, {
        onSuccess: () => toast.success("Categories reordered"),
        onError: () => {
          toast.error("Failed to save order");
          // Revert on error
          if (categories) setLocalCategories([...categories]);
        },
      });
    }
  };

  const handleCreate = () => {
    if (!formData.name.trim()) {
      toast.error("Category name is required");
      return;
    }

    // Check for duplicate name
    const exists = categories?.some(
      (c) => c.name.toLowerCase() === formData.name.toLowerCase()
    );
    if (exists) {
      toast.error("A category with this name already exists");
      return;
    }

    const newSortOrder = (categories?.length || 0);

    createCategory.mutate(
      {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        icon: formData.icon,
        sort_order: newSortOrder,
      },
      {
        onSuccess: () => {
          toast.success("Category created");
          setIsCreating(false);
          resetForm();
        },
        onError: () => toast.error("Failed to create category"),
      }
    );
  };

  const handleUpdate = () => {
    if (!editingCategory) return;

    if (!formData.name.trim()) {
      toast.error("Category name is required");
      return;
    }

    // Check for duplicate name (excluding current)
    const exists = categories?.some(
      (c) =>
        c.id !== editingCategory.id &&
        c.name.toLowerCase() === formData.name.toLowerCase()
    );
    if (exists) {
      toast.error("A category with this name already exists");
      return;
    }

    updateCategory.mutate(
      {
        id: editingCategory.id,
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        icon: formData.icon,
        is_active: formData.is_active,
      },
      {
        onSuccess: () => {
          toast.success("Category updated");
          setEditingCategory(null);
          resetForm();
        },
        onError: () => toast.error("Failed to update category"),
      }
    );
  };

  const handleDelete = () => {
    if (!deletingCategory) return;

    const count = articleCounts[deletingCategory.id] || 0;
    if (count > 0) {
      toast.error(`Cannot delete: ${count} article(s) are in this category. Reassign or delete them first.`);
      setDeletingCategory(null);
      return;
    }

    deleteCategory.mutate(deletingCategory.id, {
      onSuccess: () => {
        toast.success("Category deleted");
        setDeletingCategory(null);
      },
      onError: () => toast.error("Failed to delete category"),
    });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      icon: "HelpCircle",
      is_active: true,
    });
  };

  const openEditDialog = (category: HelpCategory) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || "",
      icon: category.icon || "HelpCircle",
      is_active: category.is_active,
    });
  };

  const isLoading = categoriesLoading || articlesLoading;

  // Loading states for auth/admin check
  if (authLoading || adminLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/admin?tab=communications")}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <FolderOpen className="h-6 w-6 text-primary" />
                Manage Categories
              </h1>
              <p className="text-muted-foreground">
                Organize and reorder help center categories
              </p>
            </div>
          </div>
          <Button onClick={() => setIsCreating(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Category
          </Button>
        </div>

        {/* Categories Table */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">
              Categories ({localCategories.length})
            </CardTitle>
            <CardDescription>
              Drag rows to reorder. Order is saved automatically.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : localCategories.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FolderOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No categories yet.</p>
                <Button
                  variant="link"
                  className="mt-2"
                  onClick={() => setIsCreating(true)}
                >
                  Create your first category
                </Button>
              </div>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]"></TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Slug</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Articles</TableHead>
                      <TableHead className="text-center">Order</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <SortableContext
                      items={localCategories.map((c) => c.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {localCategories.map((category) => (
                        <SortableRow
                          key={category.id}
                          category={category}
                          articleCount={articleCounts[category.id] || 0}
                          onEdit={openEditDialog}
                          onDelete={setDeletingCategory}
                        />
                      ))}
                    </SortableContext>
                  </TableBody>
                </Table>
              </DndContext>
            )}

            {reorderCategories.isPending && (
              <div className="flex items-center justify-center gap-2 py-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving order...
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog
          open={isCreating || !!editingCategory}
          onOpenChange={(open) => {
            if (!open) {
              setIsCreating(false);
              setEditingCategory(null);
              resetForm();
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingCategory ? "Edit Category" : "Create Category"}
              </DialogTitle>
              <DialogDescription>
                {editingCategory
                  ? "Update the category details below."
                  : "Add a new category to organize help articles."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g., Getting Started"
                />
                {formData.name && (
                  <p className="text-xs text-muted-foreground">
                    Slug: {generateSlug(formData.name)}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Brief description of this category"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="icon">Icon</Label>
                <Select
                  value={formData.icon}
                  onValueChange={(value) =>
                    setFormData({ ...formData, icon: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ICON_OPTIONS.map((icon) => (
                      <SelectItem key={icon} value={icon}>
                        {icon}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {editingCategory && (
                <div className="flex items-center gap-3">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, is_active: checked })
                    }
                  />
                  <Label htmlFor="is_active">Active</Label>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsCreating(false);
                  setEditingCategory(null);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={editingCategory ? handleUpdate : handleCreate}
                disabled={createCategory.isPending || updateCategory.isPending}
              >
                {(createCategory.isPending || updateCategory.isPending) && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {editingCategory ? "Save Changes" : "Create Category"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          open={!!deletingCategory}
          onOpenChange={(open) => !open && setDeletingCategory(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                Delete Category
              </AlertDialogTitle>
              <AlertDialogDescription>
                {deletingCategory && (
                  <>
                    Are you sure you want to delete "
                    <strong>{deletingCategory.name}</strong>"?
                    {(articleCounts[deletingCategory.id] || 0) > 0 ? (
                      <span className="block mt-2 text-destructive font-medium">
                        ⚠️ This category has{" "}
                        {articleCounts[deletingCategory.id]} article(s). You
                        must reassign or delete them first.
                      </span>
                    ) : (
                      <span className="block mt-2">
                        This action cannot be undone.
                      </span>
                    )}
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={
                  deleteCategory.isPending ||
                  (deletingCategory
                    ? (articleCounts[deletingCategory.id] || 0) > 0
                    : false)
                }
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteCategory.isPending && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
